/**
 * Component: Header
 * Author: Dine AI
 * Date: July 16, 2025
 * Last Modified: July 16, 2025
 * 
 * Purpose:
 * - Main application header with responsive navigation
 * - Handles both desktop and mobile navigation layouts
 * - Includes user authentication state display
 * - Manages dropdown menus and mobile drawer
 * - Implements scroll-based style transitions
 * 
 * Props: None (uses hooks for data)
 * 
 * State Management:
 * - Navigation state from useNavigation hook
 * - Scroll position from useScrollEffect hook
 * - Mobile detection from useIsMobile hook
 * 
 * Performance:
 * - Conditional rendering based on viewport
 * - Optimized scroll event handling
 * - Lazy loads mobile navigation component
 * 
 * Error Handling:
 * - Graceful fallback for missing user data
 * - Safe navigation state transitions
 * - Accessibility compliant keyboard navigation
 */
import { Menu, X } from "lucide-react";
import { motion } from "framer-motion";
import { useState } from "react";
import Logo from "./Logo";
import DesktopNavigation from "./header/DesktopNavigation";
import MobileNavigation from "./header/MobileNavigation";
import { useIsMobile } from "@/hooks/use-mobile";
import { useNavigation } from "@/hooks/useNavigation";
import { useScrollEffect } from "@/hooks/useScrollEffect";
import { headerVariants } from "./ui/animations";

const Header = () => {
  const isScrolled = useScrollEffect();
  const isMobile = useIsMobile();
  const {
    navLinks,
    activeDropdown,
    setActiveDropdown,
    showUserDropdown,
    setShowUserDropdown,
    mobileMenuOpen,
    setMobileMenuOpen
  } = useNavigation();
  
  // Log navigation errors
  const handleNavigationError = (error: any, action: string) => {
    console.error(`[Header] Navigation error during ${action}:`, error);
  };

  return (
    <>
    <motion.header
      variants={headerVariants}
      initial="hidden"
      animate="visible"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled 
          ? "bg-[#2256e5] py-2" 
          : "bg-[#2256e5] py-4"
      }`}
    >
      <div className="container mx-auto px-4 flex justify-between items-center">
        <Logo variant="light" />

        {!isMobile && (
          <DesktopNavigation
            navLinks={navLinks}
            activeDropdown={activeDropdown}
            setActiveDropdown={setActiveDropdown}
            showUserDropdown={showUserDropdown}
            setShowUserDropdown={setShowUserDropdown}
          />
        )}

        {isMobile && (
          <button
            type="button"
            className="md:hidden text-[#43acff] p-2 rounded-md focus:outline-none"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            {mobileMenuOpen ? (
              <X className="h-6 w-6" />
            ) : (
              <Menu className="h-6 w-6" />
            )}
          </button>
        )}
      </div>

      {isMobile && (
        <MobileNavigation
          navLinks={navLinks}
          mobileMenuOpen={mobileMenuOpen}
          activeDropdown={activeDropdown}
          setActiveDropdown={setActiveDropdown}
        />
      )}
    </motion.header>

    {/* Interactive Demo Modal */}
  </>
  );
};

export default Header;
